import { faImageSlash } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Button } from "reactstrap";
import { ImageOption } from "../../actions/Tenants/config/constantsTyped";
import { Loading } from "../Helper/Loading";
import { fetchBlobUrl } from "../scenes/image-utils";

type UseBlobUrlOptions = {
    enabled?: boolean,
};

export const useBlobUrl = (blob_name: string, options: UseBlobUrlOptions = {}) => {
    const { enabled } = options;

    return useQuery({
        queryKey: ["blobUrl", blob_name],
        queryFn: () => fetchBlobUrl(blob_name),
        enabled: enabled ?? !!blob_name,
        staleTime: 1000 * 60 * 60,
    });
};

export function BlobImage({ blob_name, alt = "", style = {}, aspectRatio: aspectRatioFromConfig }: { blob_name: string, alt: string, style?: React.CSSProperties, aspectRatio?: "square" | "landscape" | "portrait" }) {
    const { data: image_url, isLoading: isLoadingUrl } = useBlobUrl(blob_name);
    const [isImageLoaded, setImageLoaded] = useState(false);
    const imgRef = useRef<HTMLImageElement>(null);
    const timer = useRef<NodeJS.Timeout | null>(null);
    const [retryTimeout, setRetryTimeout] = useState(250);
    const [aspectRatio, setAspect] = useState<"square" | "landscape" | "portrait">(aspectRatioFromConfig || "square");

    // Show slash icon if no blob_name provided
    if (!blob_name) {
        return (
            <div style={{ ...style, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: "#f0f0f0" }}>
                <FontAwesomeIcon icon={faImageSlash} />
            </div>
        );
    }

    const loadImage = () => {
        if (!image_url || !imgRef.current) return;

        const img = imgRef.current;
        img.onload = () => {
            if (!aspectRatioFromConfig) {
                const LANDSCAPE_RATIO = 1.2;
                const PORTRAIT_RATIO = 1 / LANDSCAPE_RATIO;

                const { naturalWidth: w, naturalHeight: h } = img;

                const ratio = w / h;

                if (ratio >= LANDSCAPE_RATIO) {
                    setAspect("landscape");
                } else if (ratio <= PORTRAIT_RATIO) {
                    setAspect("portrait");
                } else {
                    setAspect("square");
                }
            }

            setImageLoaded(true);
        };
        img.onerror = () => {
            const newTimeout = retryTimeout + 250;
            setRetryTimeout(newTimeout);
            timer.current = setTimeout(() => {
                if (imgRef.current) {
                    imgRef.current.src = image_url;
                }
            }, newTimeout);
        };
        img.src = image_url;
    };

    // Cleanup timer on unmount
    useEffect(() => {
        return () => {
            if (timer.current) {
                clearTimeout(timer.current);
            }
        };
    }, []);

    // Load image when URL is available
    useEffect(() => {
        if (image_url && !isLoadingUrl) {
            setImageLoaded(false);
            loadImage();
        }
    }, [image_url, isLoadingUrl]);

    const isLoading = isLoadingUrl || !isImageLoaded;

    return (
        <div
            style={{
                ...style,
                position: "relative",
                background: "#f1f1f1",
                width: aspectRatio === "landscape" ? "75px" : "50px",
                height: aspectRatio === "portrait" ? "75px" : "50px",
                display: "inline-block",
                alignItems: "center",
                justifyContent: "center"
            }}
        >
            <div
                style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    display: isLoading ? "block" : "none"
                }}
            >
                <Loading />
            </div>

            <img
                ref={imgRef}
                alt={alt || `Img for value: ${blob_name}`}
                style={{
                    width: "100%",
                    height: "100%",
                    objectFit: aspectRatio === "square" ? "cover" : "contain",
                    display: isImageLoaded ? "block" : "none",
                    backgroundColor: "white",
                }}
            />
        </div>
    );
}

export interface ImageButtonProps {
    selected: boolean,
    onClick: () => void,
    disabled?: boolean,
    blob_name?: string,
    aspectRatio?: "square" | "landscape" | "portrait",
}

export function ImageButton({ selected, onClick, disabled, blob_name, aspectRatio }: ImageButtonProps) {
    return (
        <Button
            outline={!selected}
            disabled={disabled}
            className="me-3 mt-1"
            onClick={onClick}
            style={{ display: "inline-flex" }}
        >
            {blob_name ? (
                <BlobImage
                    blob_name={blob_name}
                    alt={`Img for value: ${selected}`}
                    aspectRatio={aspectRatio}
                />
            ) : (
                <FontAwesomeIcon icon={faImageSlash} style={{ width: "50px", height: "50px" }} />
            )}
        </Button>
    );
}

export interface ImageRadioButtonsProps {
    options: ImageOption[],
    value: string,
    setValue: (value: number | undefined) => void,
    aspectRatio?: "square" | "landscape" | "portrait",
}

export function ImageRadioButtons({ options, value, setValue, aspectRatio }: ImageRadioButtonsProps) {
    return (
        <div>
            {options.map(({ value: optionValue, disabled, blob_name }, index) => (
                <ImageButton
                    key={index}
                    selected={String(value) === String(optionValue)}
                    disabled={disabled}
                    blob_name={blob_name}
                    onClick={() => {
                        const newValue: number | undefined = String(value) === String(optionValue) ? undefined : (optionValue as unknown) as number;
                        if (!newValue) (document.activeElement as any).blur();
                        setValue(newValue);
                    }}
                    aspectRatio={aspectRatio}
                />
            ))}
        </div>
    );
}
